import { z } from 'zod';
import { TenantId } from './ids.js';
/**
 * **The support session** — "see what she sees" (docs/status-2026-09-21.md §5;
 * plan, sixth pass X5): a short-lived, read-only, audited staff seat the
 * operator console mints for one tenant so an operator can open the staff app
 * as that business and look at exactly the screens its owner is looking at.
 *
 * The shape lives *here* rather than in either service because three
 * programs have to agree on it and apps do not cross-import (dev.md §3):
 * `admin-api` signs it, `core-api` verifies it, and `apps/app` reads two
 * claims out of it to draw the banner and to know when to stop. One schema,
 * one set of names, and a drift on any side is a failing parse rather than a
 * silently accepted token.
 *
 * It is deliberately **not** a Firebase token and cannot become one:
 *
 *  * it is an HS256 JWS under a secret only the two services hold
 *    (`SUPPORT_SESSION_SECRET`), with its own `typ` and audience, so
 *    `core-api`'s Firebase verifier never sees it and it can never be mistaken
 *    for a seat somebody provisioned;
 *  * it carries `role: readonly` **as a literal** — the verifier forces the
 *    role regardless, so nothing that reads the claim can widen it;
 *  * it has **no refresh**. Fifteen minutes is the whole life of it. The
 *    console mints another one, which writes another audit row, which is the
 *    point: the trail says *who looked, at whom, and when*, once per look.
 *
 * `sub` is `support:<operator uid>` so a log line traced by uid reads as a
 * support seat at a glance, and never as a staff member the business employs.
 */
export const SUPPORT_SESSION_ISSUER = 'yourstore:admin-api';
export const SUPPORT_SESSION_AUDIENCE = 'yourstore:core-api:support-session';
/** The JOSE `typ`; `core-api` dispatches on it before touching any key. */
export const SUPPORT_SESSION_TYP = 'support+jwt';
/**
 * Fifteen minutes, and a constant rather than a variable. It is long enough to
 * read a screen beside an owner on the phone and short enough that a tab left
 * open on a laptop is not a standing window into her book. The verifier also
 * refuses any token whose own `exp - iat` exceeds it, so a mint that widened
 * the number would be refused at the door rather than honoured.
 */
export const SUPPORT_SESSION_TTL_SECONDS = 15 * 60;
/**
 * HS256 wants a key of at least the hash width. A shorter secret is a
 * configuration error at boot on both services, not a weaker token.
 */
export const SUPPORT_SESSION_SECRET_MIN_LENGTH = 32;
/**
 * The URL fragment key the console hands the credential to the staff app
 * under: `https://app…/#support_session=<token>`. A **fragment, never a query
 * string**: a query string is sent to the server and lands verbatim in the
 * load balancer's and Cloud Run's request logs, which is the exact class of
 * leak obligation K16 exists to prevent. A fragment never leaves the browser.
 */
export const SUPPORT_SESSION_FRAGMENT_KEY = 'support_session';
export const SupportSessionClaims = z.object({
    iss: z.literal(SUPPORT_SESSION_ISSUER),
    aud: z.literal(SUPPORT_SESSION_AUDIENCE),
    /** `support:<operator uid>`. */
    sub: z.string().min(1),
    tenant_id: TenantId,
    role: z.literal('readonly'),
    support: z.object({
        /** The operator's Firebase uid — opaque, never an email. */
        operator: z.string().min(1),
        /** ISO instant, the same value written to `audit_log.at`'s row. */
        minted_at: z.string().min(1),
        /** The `audit_log` row that records this mint, so the two can be joined. */
        audit_id: z.string().min(1),
    }),
    iat: z.number().int(),
    exp: z.number().int(),
    jti: z.string().min(1),
});
export function supportSessionSubject(operatorUid) {
    return `support:${operatorUid}`;
}
/**
 * What a staff session carries about the support seat it is running under —
 * the projection `GET /v1/session` answers and the banner draws. Two facts,
 * because the banner has two things to say: whose eyes these are, and when
 * they close.
 */
export const SupportSessionView = z.object({
    operator: z.string().min(1),
    expires_at: z.string().min(1),
});
//# sourceMappingURL=auth.js.map